// Shared helper for appending a synthesized DDL statement after the source
// statement. `StatementInfo.range` may stop at the statement body and leave
// a trailing `;` outside the range — when that's the case we insert AFTER
// the `;` so the original terminator remains attached to the source
// statement. Otherwise we insert at the range end with a leading `;\n`.
//
// Used by the column-edit ALTER TABLE source path AND the index-edit
// applicator's standalone / mysql ALTER TABLE append paths. Promoted from
// columnEdit.alterTable.ts per the Codebase Patterns "Applicators" hand-off
// when the index applicator slice landed.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: Index Edit Applicator.

export interface AppendInsertion {
  readonly offset: number;
  readonly sep: string;
  /** Trailing separator to append AFTER the synthesized statement body —
   *  `';\n'` when there is additional non-whitespace content after the
   *  insertion point (i.e. the buffer already has prior-appended statements
   *  following the source statement), `''` otherwise. Callers must use
   *  `sep + synth + trailSep` as the `replacement` text so that the inserted
   *  statement is properly terminated and parseable in a subsequent
   *  extract → apply → emit chain step. */
  readonly trailSep: string;
}

/** Pick the offset to insert the appended synthesized DDL statement at, plus
 *  the separator to put between source and synth.
 *
 *  When there is additional non-whitespace content after the insertion point
 *  (because a prior `applyDdlEdit` call already appended one or more
 *  statements), `trailSep` is `';\n'` so the newly inserted statement is
 *  terminated and parseable on a subsequent re-extract. `trailSep` is `''`
 *  when the insert is at the end of the buffer (single-edit scenario). */
export function pickAppendInsertion(source: string, endOffset: number): AppendInsertion {
  let p = endOffset;
  while (p < source.length) {
    const c = source[p];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { p++; continue; }
    break;
  }
  if (p < source.length && source[p] === ';') {
    // Look ahead past the ';' to see if there is more content in the buffer.
    let q = p + 1;
    while (q < source.length) {
      const c = source[q];
      if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { q++; continue; }
      break;
    }
    const hasTrailingContent = q < source.length;
    return { offset: p + 1, sep: '\n', trailSep: hasTrailingContent ? ';\n' : '' };
  }
  return { offset: endOffset, sep: ';\n', trailSep: '' };
}
